import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStateData } from '@/lib/stateData';
import SEO from '@/components/seo/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink, MapPin, CloudRain, FileText, ArrowRight, Scale, Phone, Building, History, CheckCircle2, AlertTriangle, TrendingUp } from 'lucide-react';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { trackExternalHandoff } from '@/lib/tracking';
import { trackSignupIntent } from '@/lib/tracking';
import { getSignupUrl } from '@/lib/appLinks';

const BMP_MAPPING: Record<string, string> = {
    'Silt Fences & Perimeters': 'silt-fence',
    'Construction Entrances': 'stabilized-construction-entrance',
    'Concrete Washout Areas': 'concrete-washout',
    'Inlet Protection': 'inlet-protection',
    'Check Dams': 'check-dams'
};

const StateGuidePage: React.FC = () => {
  const { stateSlug } = useParams<{ stateSlug: string }>();
  const data = getStateData(stateSlug || 'general');
  
  // OPTIMIZED FOR LLM / AI OVERVIEW
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${data.name} SWPPP Requirements & NOI Filing Guide`,
    "description": `Complete guide to ${data.permitName} for construction operators. Includes inspection frequency, rain triggers, and NOI filing links for ${data.name}.`,
    "author": {
        "@type": "Organization",
        "name": "RunOffTrack",
        "url": "https://runofftrack.com"
    },
    "about": {
        "@type": "GovernmentOrganization",
        "name": data.authorityName,
        "alternateName": data.abbreviation
    },
    "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://runofftrack.com/swppp-requirements/${data.slug}`
    },
    ...(data.lastReviewed ? { "dateModified": data.lastReviewed } : {}),
    "mainEntity": [
        {
            "@type": "Question",
            "name": `How often must I inspect construction sites in ${data.name}?`,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": data.inspectionFrequency
            }
        },
        {
            "@type": "Question",
            "name": `What is the rain trigger for SWPPP inspections in ${data.name}?`,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": `In ${data.name}, an inspection is triggered by ${data.rainTrigger}.`
            }
        }
    ]
  };

  const handleTrackHandoff = (url: string, type: 'noi_portal' | 'agency_site' | 'contact') => {
      trackExternalHandoff(url, type);
  };

  // Dynamic Prose Generation based on data rules
  const getFrequencyAnalysis = () => {
      const freq = data.inspectionFrequency.toLowerCase();
      if (freq.includes('twice')) {
          return "Build the twice-weekly requirement into the site calendar and preserve the required spacing between inspections. Check the permit for reduced-frequency provisions before changing that cadence.";
      }
      if (freq.includes('14 days')) {
          return "A 14-day schedule may be paired with an event-driven inspection requirement. Track both the recurring deadline and qualifying weather events so one schedule does not obscure the other.";
      }
      if (freq.includes('within') || freq.includes('post-') || freq.includes('post-rain')) {
          return "This schedule combines recurring and event-driven duties. Record rainfall or discharge events independently from the weekly calendar and document why each event did or did not trigger an inspection.";
      }
      return "Use the permit-defined schedule as the baseline and document each completed inspection. Project size, receiving water, stabilization status, or local approvals may change the frequency shown here.";
  };

  return (
    <div className="page-shell">
      <SEO 
        title={`${data.name} SWPPP Requirements & NOI Filing Guide`}
        description={`File your ${data.name} Notice of Intent (NOI). ${data.name} Construction General Permit requirements, ${data.authorityName} contact info, and inspection checklists.`}
        canonical={`/swppp-requirements/${data.slug}`}
        jsonLd={jsonLd}
      />

      <div className="page-intro pb-16 pt-20 md:pb-20 md:pt-24">
        <div className="container relative z-10 px-4">
           
           <div className="max-w-4xl mx-auto">
               <Breadcrumbs items={[
                   { label: 'State Directory', href: '/swppp-requirements' },
                   { label: data.name, href: `/swppp-requirements/${data.slug}` }
               ]} />

               <div className="text-center">
                   <Badge variant="outline" className="mb-6 rounded-full border-primary/20 bg-primary/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-primary">
                      State Compliance Reference
                   </Badge>
                   <h1 className="mb-6 text-4xl font-black tracking-[-0.045em] text-foreground md:text-6xl">
                      {data.name} SWPPP Compliance
                   </h1>
                   
                   <div className="mx-auto mb-8 max-w-2xl rounded-2xl border border-border/70 bg-card p-6 text-left shadow-lg shadow-foreground/5">
                        <p className="font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-2">Quick Facts for {data.name}</p>
                        <ul className="space-y-2 text-sm md:text-base">
                            <li><strong>Authority:</strong> {data.authorityName} ({data.abbreviation})</li>
                            <li><strong>Permit:</strong> {data.permitName}</li>
                            <li><strong>Inspection Schedule:</strong> {data.inspectionFrequency}</li>
                            <li><strong>Rain Trigger:</strong> {data.rainTrigger}</li>
                        </ul>
                   </div>

                   <div className="flex flex-col sm:flex-row justify-center gap-4">
                      <Button size="lg" className="rounded-full px-8 shadow-lg" asChild>
                        <a href={data.permitLink} target="_blank" rel="noopener noreferrer">View Official Permit <ExternalLink className="ml-2 h-4 w-4" /></a>
                      </Button>
                      <Button size="lg" variant="outline" className="rounded-full px-8 bg-background" asChild>
                        <Link to="/tools/fine-calculator">Calculate Fine Risk</Link>
                      </Button>
                   </div>
               </div>
           </div>
        </div>
      </div>

      <div className="container px-4 py-16 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-3 gap-8">
            
            <div className="lg:col-span-2 space-y-12">
                
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900/30 dark:text-slate-300">
                    This page is a field reference, not legal advice. Confirm the current permit, local requirements, and project-specific conditions with {data.authorityName} before relying on a deadline or inspection schedule.
                    {data.lastReviewed && <span className="mt-2 block text-xs font-medium">Official sources last reviewed: {data.lastReviewed}</span>}
                </div>

                <section>
                    <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                        <MapPin className="text-primary" /> Regulation Overview
                    </h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Card className="bg-card/50">
                            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Permitting Authority</CardTitle></CardHeader>
                            <CardContent className="font-semibold">{data.authorityName}</CardContent>
                        </Card>
                        <Card className="bg-card/50">
                            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Official Permit</CardTitle></CardHeader>
                            <CardContent className="font-semibold text-sm">{data.permitName}</CardContent>
                        </Card>
                        <Card className="bg-card/50">
                            <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Inspection Frequency</CardTitle></CardHeader>
                            <CardContent className="font-semibold">{data.inspectionFrequency}</CardContent>
                        </Card>
                        <Card className="bg-blue-50/50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900">
                            <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-700 dark:text-blue-300 font-medium">Rain Event Trigger</CardTitle></CardHeader>
                            <CardContent>
                                <div className="font-bold text-foreground mb-2">{data.rainTrigger}</div>
                                <Link to="/tools/rain-event-finder" className="text-xs flex items-center gap-1 text-blue-600 hover:underline">
                                    <History size={12} /> Check Missed Events
                                </Link>
                            </CardContent>
                        </Card>
                    </div>
                </section>

                {/* EPA Comparison Card */}
                {data.complianceNuance && (
                    <div className="bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 rounded-xl p-6">
                        <div className="flex items-start gap-4">
                            <div className="bg-slate-200 dark:bg-slate-800 p-2 rounded-lg text-slate-700 dark:text-slate-300">
                                <Scale size={24} />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
                                    State vs. Federal (EPA)
                                    <Badge variant={data.complianceNuance.epaComparison === 'Stricter than EPA' ? 'destructive' : 'secondary'} className="text-xs">
                                        {data.complianceNuance.epaComparison}
                                    </Badge>
                                </h3>
                                <p className="font-semibold mt-1">{data.complianceNuance.title}</p>
                                <p className="text-slate-600 dark:text-slate-400 mt-2 text-sm">
                                    {data.complianceNuance.description}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <section className="prose dark:prose-invert max-w-none">
                    <h2 className="text-2xl font-bold mb-4">Inspection Requirements for {data.name}</h2>
                    <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                        Construction projects that disturb one or more acres—or smaller sites that are part of a larger common plan—commonly require stormwater permit coverage in {data.name}. Eligibility, exemptions, local approvals, and project-specific conditions vary, so confirm coverage under the {data.permitName} before construction begins.
                    </p>
                    
                    {/* Dynamic Insight Block */}
                    <div className="bg-purple-50 dark:bg-purple-900/10 border-l-4 border-purple-500 p-6 rounded-r-lg mb-8">
                        <h3 className="text-lg font-bold text-purple-800 dark:text-purple-300 mb-2 flex items-center gap-2">
                            <TrendingUp size={20}/> Operational Insight
                        </h3>
                        <p className="text-purple-700 dark:text-purple-200">
                            {getFrequencyAnalysis()}
                        </p>
                    </div>
                    
                    <div className="bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
                        <h3 className="text-lg font-bold text-blue-800 dark:text-blue-300 mb-2 flex items-center gap-2">
                            <CloudRain size={20}/> Specific State Nuance
                        </h3>
                        <p className="text-blue-700 dark:text-blue-200">
                            {data.notes}
                        </p>
                    </div>

                    <h3 className="text-xl font-bold mb-4">Common BMPs Checked in {data.name}</h3>
                    <ul className="grid sm:grid-cols-2 gap-3 not-prose mb-8">
                        {['Silt Fences & Perimeters', 'Construction Entrances', 'Concrete Washout Areas', 'Inlet Protection', 'Stockpile Management', 'Dust Control'].map(bmpName => {
                            const slug = BMP_MAPPING[bmpName];
                            return (
                                <li key={bmpName} className="group relative">
                                    {slug ? (
                                        <Link 
                                            to={`/resources/bmp-field-guide/${slug}`} 
                                            className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border hover:border-primary/50 transition-all hover:bg-card hover:shadow-sm"
                                        >
                                            <div className="flex items-center gap-2">
                                                <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />
                                                <span className="text-sm font-medium text-foreground group-hover:text-primary">{bmpName}</span>
                                            </div>
                                            <ArrowRight size={14} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </Link>
                                    ) : (
                                        <div className="flex items-center gap-2 p-3 bg-muted/20 rounded-lg border border-border opacity-80">
                                            <CheckCircle2 size={16} className="text-green-500 flex-shrink-0" />
                                            <span className="text-sm font-medium">{bmpName}</span>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </section>

                <div className="bg-primary text-primary-foreground p-8 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-6 shadow-xl">
                    <div>
                        <h3 className="text-2xl font-bold mb-2">Continue your field review</h3>
                        <p className="text-primary-foreground/80 max-w-md">
                            Check common controls in the BMP field guide, then compare them with your approved SWPPP.
                        </p>
                    </div>
                    <div className="flex w-full flex-col gap-2 sm:w-auto">
                        <Button variant="secondary" size="lg" className="whitespace-nowrap" asChild>
                            <a href={getSignupUrl()} onClick={() => trackSignupIntent('state_guide_bottom')}>Create a Free Inspection</a>
                        </Button>
                        <Link to="/resources/bmp-field-guide" className="text-center text-sm font-semibold text-primary-foreground/85 underline underline-offset-4">Open BMP Field Guide</Link>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                {/* Regulatory Filing Center Card */}
                <Card className="border-border shadow-md bg-slate-50 dark:bg-slate-900/50">
                    <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                            <Building size={18} className="text-primary" /> Regulatory Filing
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <p className="text-xs font-semibold text-muted-foreground uppercase">File Notice of Intent (NOI)</p>
                            {data.noiLink ? (
                                <a 
                                    href={data.noiLink} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="block w-full"
                                    onClick={() => handleTrackHandoff(data.noiLink!, 'noi_portal')}
                                >
                                    <Button variant="outline" className="w-full gap-2 border-primary/20 text-primary hover:bg-primary/5">
                                        Open {data.abbreviation} Portal <ExternalLink size={14} />
                                    </Button>
                                </a>
                            ) : (
                                <div className="text-sm text-muted-foreground italic">Portal URL not available</div>
                            )}
                        </div>

                        {data.agencyContact && (
                            <>
                                <div className="h-px bg-border/50"></div>
                                <div className="space-y-2">
                                    <p className="text-xs font-semibold text-muted-foreground uppercase">Agency Contact</p>
                                    {data.agencyContact.phone && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <Phone size={14} className="text-muted-foreground"/> 
                                            <a 
                                                href={`tel:${data.agencyContact.phone}`} 
                                                className="hover:underline"
                                                onClick={() => handleTrackHandoff(data.agencyContact!.phone!, 'contact')}
                                            >
                                                {data.agencyContact.phone}
                                            </a>
                                        </div>
                                    )}
                                    {data.agencyContact.email && (
                                        <div className="flex items-center gap-2 text-sm">
                                            <ExternalLink size={14} className="text-muted-foreground"/> 
                                            <a 
                                                href={`mailto:${data.agencyContact.email}`} 
                                                className="hover:underline truncate"
                                                onClick={() => handleTrackHandoff(data.agencyContact!.email!, 'contact')}
                                            >
                                                {data.agencyContact.email}
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </CardContent>
                </Card>

                <Card className="border-border shadow-sm">
                    <CardHeader>
                        <CardTitle className="text-lg">Official Resources</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <a 
                            href={data.permitLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group"
                            onClick={() => handleTrackHandoff(data.permitLink, 'agency_site')}
                        >
                            <div className="bg-primary/10 p-2 rounded-md text-primary"><FileText size={18}/></div>
                            <div>
                                <div className="font-medium text-sm group-hover:text-primary transition-colors">Official Permit Text</div>
                                <div className="text-xs text-muted-foreground">Direct link to {data.abbreviation} gov site</div>
                            </div>
                        </a>
                        <Link to="/tools/fine-calculator" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors group">
                            <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-md text-red-600"><AlertTriangle size={18}/></div>
                            <div>
                                <div className="font-medium text-sm group-hover:text-red-600 transition-colors">Fine Risk Calculator</div>
                                <div className="text-xs text-muted-foreground">Calculate potential penalties</div>
                            </div>
                        </Link>
                    </CardContent>
                </Card>

                <div className="p-6 bg-muted/30 rounded-xl border border-border">
                    <h4 className="font-bold mb-4 flex items-center gap-2"><FileText size={18}/> Why Go Digital?</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                        Handwritten logs are hard to read and easy to lose. Digital reports provide a timestamped, geo-tagged audit trail that protects your business.
                    </p>
                    <div className="text-xs font-mono bg-background p-3 rounded border text-muted-foreground">
                        "The best defense against an EPA audit is a perfect paper trail."
                    </div>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default StateGuidePage;
